import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';
import DAMImageUtil from '../../global/dam-image-util';
import { default as languageStrings } from '../../global/l10n/ToolSnapshot.json';
import Util from '../../global/util';
import ToolDao from './tool-snapshot.dao';
import { ParentToolType, ToolDisplayType, ToolMatch } from './tool-snapshot.interface';

@Component({
    tag: 'ti-tool-snapshot',
    styleUrl: 'tool-snapshot.scss',
    shadow: true
})
export class ToolSnapshot {

    private _commonQueryString: string = 'keyMatch={searchTerm}&tisearch={tisearch}&usecase={toolUseCase}';
    private _loginUrl = '/secure-link-forward/?gotoUrl=';
    private _refDesignImage = '/content/dam/ticom/images/icons/svg-icons/reference-designs-icon.svg';
    private _snapshotUrl: string;

    /**
     * Load Statuses
     */
    private _statusType = {
        LOADING: 'loading',
        READY: 'ready',
        ERROR: 'error'
    }

    /**
     * The element for this component
     */
    @Element() _hostElement: HTMLTiToolSnapshotElement;

    /**
     * global utility function to format price
     *
     */
    @State() currencyFormat: any = { convertAndFormat: price => price };

    /**
     * Holds tool response data
     *
     * @type {ToolMatch}
     */
    @State() toolMatchDetails: ToolMatch;

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @type {string}
     * @default _statusType.Loading
     */
    @State() status: string = this._statusType.LOADING;
    @Watch('status')
    handleComponentStatusUpdate(newValue: string, oldValue: string) {
        if (newValue !== oldValue && newValue === this._statusType.READY) {
            this.componentRendered.emit({
                tag: this._hostElement.tagName,
                toolGroup: this.toolMatchDetails.toolGroup
            });
        }
    }

    /**
     * Value for tisearch metrics parameter
     *
     * @type {string}
     * @default 'universal_search'
     */
    @Prop() tisearch: string = 'universal_search';

    /**
     * This locale is used for price formatting
     * it can be korean, german, english, chinese, japanese
     * while the main locale can be only english, chinese, japanese
     *
     * @type {string}
     * @default null
     */
    @Prop({ mutable: true }) currencyLocale: string = null;

    /**
     * Currency value
     *
     * @type {string}
     * @default null
     */
    @Prop({ mutable: true }) currency: string = null;

    /**
     * Default image to be displayed if the image for the gpn is not found.
     * Should be a relative path from the server root.
     *
     * @type {string}
     * @default '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg'
     */
    @Prop() defaultImage: string = '/content/dam/ticom/images/icons/svg-icons/no-image-available-icon.svg';

    /**
     * URL for the image api
     *
     * @type {string}
     * @default '/imagemetadata/api'
     */
    @Prop() imageApiUrl: string = '/imagemetadata/api';

    /**
    * tool part number value
    *
    * @type {string}
    */
    @Prop({mutable: true}) toolPartNumber: string;

    /**
     * Value of locale
     *
     * @type {string}
     * @default 'en-US'
     */
    @Prop() locale: string = 'en-US';

    /**
     * root URL for ti.com
     * @type {string}
     * @default ''
     */
    @Prop() tiHost: string = '';

    /**
     * Store host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com"
     */
    @Prop() tiStoreHost: string = 'https://www.ti.com';

    /**
     * Store CN Host value for add-to-cart
     *
     * @type {string}
     * @default "https://www.ti.com.cn"
     */
    @Prop() tiStoreHostCn: string = 'https://www.ti.com.cn';

    /**
     * URL for the search api
     * @type {string}
     * @default '/search/tool'
     */
    @Prop() searchApiUrl: string = '/search/tool';

    /**
     * URL for the exchange rates api
     * @type {string}
     * @default '/search/exchangeRates'
     */
    @Prop() exchangeRateApiUrl: string = '/search/exchangeRates';

    /**
     * Catch link clicks and attach analytics query parameters
     * @param event
     */
    @Listen('click')
    clickHandler(event: any) {
        let tag = (event.composedPath && event.composedPath()[0]) || event.target as HTMLElement;
        // synopsis image click
        if (tag.className === 'ti-image-object') {
            tag = tag.parentElement.parentElement;
        }
        if (tag.tagName.toLowerCase() === 'a' && tag.getAttribute('href')) {
            event.preventDefault();
            const url = Util.formatUrl(
                tag.getAttribute('href'),
                {
                    searchTerm: this.toolPartNumber,
                    tisearch: this.tisearch.toLocaleLowerCase(),
                    toolUseCase: this.toolMatchDetails.toolGroup
                },
                this._commonQueryString
            );
            window.location.href = url;
        }
    }

    /**
     * Method to set the tool data
     */
    @Method()
    public async updateData(toolMatch: ToolMatch) {
        if (toolMatch) {
            this.toolMatchDetails = toolMatch;
            this.toolPartNumber = toolMatch.toolPartNumber;
            this.status = this._statusType.READY
        }
        else {
            this.status = this._statusType.ERROR;
        }
    }

    /**
     * Event for when component section has rendered
     */
    @Event() componentRendered: EventEmitter;

    async componentWillLoad() {
        if (this.toolPartNumber) {
            this._updateData();
        }

        // validate locale
        // ! Tool snapshot supports limited locales to match tool folder
        if (['en-US', 'ja-JP', 'zh-CN'].indexOf(this.locale) === -1) {
            this.locale = 'en-US';
        }
        // currency
        const win = typeof (window as any) !== 'undefined' ? window : {} as Window;
        const com = (win as any).com;
        const userPref = (com && com.TI && com.TI.UserPreferences) ? com.TI.UserPreferences : {};
        if (this.currencyLocale == null) {
            this.currencyLocale = userPref.getUserLanguagePreference ? await userPref.getUserLanguagePreference() : 'en-US';
        }
        if (this.currency == null) {
            this.currency = userPref.getUserCurrencyPreference ? await userPref.getUserCurrencyPreference() : 'USD';
        }
        this._setCurrencyFormatter();
    }

    /**
     * Listen to the `currencyChange` event of header-currency-selection
     *
     * @param event
     */
    @Listen('tiCurrencyChange', { target: 'window' })
    async onCurrencyChange(event) {
        this.currency = event.detail;
    }

    /**
     * Listen to the `currencyReady` event of header-currency-selection
     *
     * @param event
     */
    @Listen('tiCurrencyReady', { target: 'window' })
    async onCurrencyReady(event) {
        this.currency = event.detail;
    }

    /**
     * Listen to languageChange event
     */
    @Listen('tiLanguageChange', { target: 'window' })
    onTiLanguageChange(event: CustomEvent) {
        this.currencyLocale = event.detail.locale;
    }

    /**
     * Listen to languageChange event
     */
    @Listen('tiLanguageReady', { target: 'window' })
    onTiLanguageReady(event: CustomEvent) {
        this.currencyLocale = event.detail.locale;
    }

    /**
     * Listen to the `tiCurrencyFormatReady` event from the header
     *
     */
    @Listen('tiCurrencyFormatReady', { target: 'window' })
    onCurrencyFormatReady() {
        this._setCurrencyFormatter();
    }

    private _setCurrencyFormatter(): void {
        // accessing the global utility function for formatting from window variable
        const win = typeof (window as any) !== 'undefined' ? window : {} as Window;
        const com = (win as any).com;
        if (com && com.TI && com.TI.CurrencyFormat) {
            this.currencyFormat = com.TI.CurrencyFormat;
        }
    }

    private async _updateData() {
        this.updateData(await ToolDao.searchToolMatch(this.toolPartNumber, this.locale, this.exchangeRateApiUrl,
            this.searchApiUrl, this.tiHost));
    }

    async componentWillRender() {
        if (this.status !== this._statusType.READY) {
            return;
        }
        // hardware development and not 3p
        if (this.toolMatchDetails.parentToolTypeId === ParentToolType.HARDWARE_DEVELOPMENT && !this.toolMatchDetails.isPartnerResource) {
            this._snapshotUrl = await DAMImageUtil.getInstance(this.imageApiUrl).getFirstImageUrl(this.toolMatchDetails.toolPartNumber, this.locale, 'gpn', 'TI BOARD PHOTOGRAPHY', 'small');
        } else if (this.toolMatchDetails.parentToolTypeId == ParentToolType.REFERENCE_DESIGNS) { // ref design
            this._snapshotUrl = `${this.tiHost}${this._refDesignImage}`;
        } else {
            this._snapshotUrl = `${this.tiHost}${this.toolMatchDetails.imageUrl}`;
        }
        this._snapshotUrl = this._snapshotUrl || `${this.tiHost}${this.defaultImage}`;
    }

    render() {
        if (this.status !== this._statusType.READY) {
            return ''
        }
        return (
            <Host data-lid={`universal_search_${this.toolMatchDetails.toolGroup}_snapshot_section`}>
                <ti-card appearance="plain-white">
                    <div class="ti-tools-snapshot">
                        <div class="ti-tools-snapshot-fixed">
                            <ti-product-synopsis
                                product-url={this.toolMatchDetails.toolFolderUrl}
                                image-url={`${this._snapshotUrl}`}
                                default-image={`${this.tiHost}${this.defaultImage}`}>
                                <span slot="product-family">
                                    {this.toolMatchDetails.isPartnerResource &&
                                        <span>{languageStrings[this.locale]['partnerResource']} - </span>
                                    }
                                    {this.toolMatchDetails.toolType}
                                </span>
                                <a slot="product-link" href={this.toolMatchDetails.toolFolderUrl}>{this.toolMatchDetails.toolPartNumber}</a>
                                <a slot="description" href={this.toolMatchDetails.toolFolderUrl} innerHTML={this.toolMatchDetails.description}></a>
                                {this.toolMatchDetails.isPartnerResource &&
                                    <span slot="bottom">
                                        <span>{languageStrings[this.locale]['fromPartner']} </span>
                                        <a href={this.toolMatchDetails.partnerFolderUrl} target='_blank'>{this.toolMatchDetails.supplierName}</a>
                                    </span>
                                }
                            </ti-product-synopsis>
                        </div>
                        <div class="ti-tools-snapshot-status">
                            {(this.toolMatchDetails.displayType == ToolDisplayType.ViewDetails ||
                                this.toolMatchDetails.displayType == ToolDisplayType.MultipleBoards) &&
                                <ti-button appearance="compact primary">
                                    <a href={this.toolMatchDetails.toolFolderUrl}>{languageStrings[this.locale]["viewDetails"]}</a>
                                </ti-button>
                            }
                            {this.toolMatchDetails.displayType == ToolDisplayType.Request &&
                                <div>
                                    {this.toolMatchDetails.orderingInfo.price &&
                                        <span class="ti-tool-snapshot-price ">
                                            {this.currencyFormat.convertAndFormat(this.toolMatchDetails.orderingInfo.price, this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] ?
                                                this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] : 1, false, this.currency, this.currencyLocale)}{` (${this.currency})`}
                                        </span>
                                    }
                                    <ti-button appearance="compact primary">
                                        <a href={this.toolMatchDetails.orderingInfo.orderOnlineUrl}>{this.toolMatchDetails.orderingInfo.orderButtonName}</a>
                                    </ti-button>
                                </div>
                            }

                            {this.toolMatchDetails.displayType == ToolDisplayType.AddToCart &&
                                <div>
                                    <ti-add-to-cart
                                        data-lid="add_to_cart_modal"
                                        gpn={this.toolMatchDetails.toolPartNumber}
                                        opn={this.toolMatchDetails.orderingInfo.toolOrderableNumber}
                                        show-quantity-input="true"
                                        order-limit={this.toolMatchDetails.inventory.orderLimit < 999999 ? this.toolMatchDetails.inventory.orderLimit : 0}
                                        application-type="TF"
                                        part-type="tool"
                                        ti-metrics-location="ti.com-toolsearch"
                                        locale={this.locale}
                                        currency-locale={this.currencyLocale}
                                        currency-exchange-rate-json={JSON.stringify(this.toolMatchDetails.orderingInfo.currencyExchangeRate)}
                                        ti-store-url={this.tiStoreHost}
                                        ti-store-china-url={this.tiStoreHostCn}
                                        appearance="primary compact"
                                        show-currency-code="true"
                                        show-extended-precision-price="false">
                                    </ti-add-to-cart>
                                    {this.toolMatchDetails.orderingInfo.price &&
                                        <span class="ti-tool-snapshot-price">
                                            {this.currencyFormat.convertAndFormat(this.toolMatchDetails.orderingInfo.price, this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] ?
                                                this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] : 1, false, this.currency, this.currencyLocale)}{` (${this.currency})`}
                                        </span>
                                    }
                                    <span class="ti-tool-snapshot-store-info">{languageStrings[this.locale]["inStock"]}</span>
                                    {this.toolMatchDetails.inventory.orderLimit < 999999 &&
                                        <span class="ti-tool-snapshot-store-info">
                                            {languageStrings[this.locale]["limit"]} {this.toolMatchDetails.inventory.orderLimit.toLocaleString()}
                                        </span>
                                    }
                                </div>
                            }
                            {this.toolMatchDetails.displayType == ToolDisplayType.LoggedOut &&
                                <div>
                                    <ti-button appearance="compact primary">
                                        <a href={`${this.tiHost}${this._loginUrl}${encodeURIComponent(window.location.href)}`}
                                            data-navtitle={languageStrings['en-US']["loginToOrder"]} data-lid={`indigo-${this.toolMatchDetails.toolPartNumber}`}>
                                            {languageStrings[this.locale]["loginToOrder"]}
                                        </a>
                                    </ti-button>
                                    {this.toolMatchDetails.orderingInfo.price &&
                                        <span class="ti-tool-snapshot-price">
                                            {this.currencyFormat.convertAndFormat(this.toolMatchDetails.orderingInfo.price, this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] ?
                                                this.toolMatchDetails.orderingInfo.currencyExchangeRate[this.currency] : 1, false, this.currency, this.currencyLocale)}{` (${this.currency})`}
                                        </span>
                                    }
                                    <a class="ti-tool-snapshot-availability" href={`${this.tiHost}${this._loginUrl}${encodeURIComponent(window.location.href)}`}
                                        data-navtitle={languageStrings['en-US']["loginToViewInventory"]} data-lid={`indigo-${this.toolMatchDetails.toolPartNumber}`}>
                                        <ti-svg-icon icon-set="objects" appearance="secondary" size="s">lock</ti-svg-icon>
                                        <span>{languageStrings[this.locale]["loginToViewInventory"]}</span>
                                    </a>
                                </div>
                            }
                            {this.toolMatchDetails.displayType == ToolDisplayType.NotAvailable &&
                                <span class="ti-tool-snapshot-price full-width">{languageStrings[this.locale]["notAvailable"]}</span>
                            }
                            {this.toolMatchDetails.displayType == ToolDisplayType.OutOfStock &&
                                <div class="full-width">
                                    <span class="ti-tool-snapshot-price">{languageStrings[this.locale]["outOfStock"]}</span>
                                    <ti-button appearance="link"><ti-notify-me locale={this.locale}
                                        opn={this.toolMatchDetails.toolPartNumber}>
                                    </ti-notify-me></ti-button>
                                </div>
                            }
                        </div>

                    </div>
                </ti-card>
            </Host>
        )
    }
}
